import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';

import customAxios from '../../../utils/customAxios';
import { GlobalVariables } from '../../../globalVar';
import { getToast } from '../../../utils/toastConfig';
import { useTableLocale } from '../../../hooks/useTableLocale';
import usePersistedColumnOrder from '../../../hooks/UseCustomColumnOrder';
import { AutocompleteFilterInput } from '../../Utils/AutocompleteFilterInput';
import { ExternalLinkWithIcon } from '../../UI/ExternalLinkWithIcon';
import { useUserPermissions } from '../../../hooks/userPermissions';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

export const EquipmentsMaintenanceTable = () => {
  const permissions = useUserPermissions();
  const [isFetching, setIsFetching] = useState(false); // State for tracking API request status
  const [data, setData] = useState([]); // State for storing the fetched data

  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: 'and',
    quickFilterValues: [],
    quickFilterLogicOperator: 'and',
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      const endpoint = `${GlobalVariables.getURL()}equipment-maintenance/movements`;
      try {
        const response = await customAxios.post(
          endpoint,
          {
            pagination: {
              rowsPerPage: paginationModel.pageSize,
              page: paginationModel.page + 1,
            },
            filters: filterModel,
          },
          HEADERS
        );

        if (response.status === 204) {
          setData([]);
          setRowCount(0);
          return;
        }

        const movements = (response.data.data.movements || []).map((item) => {
          let equipmentType = '';
          try {
            const eq =
              typeof item.equipment_type === 'string'
                ? JSON.parse(item.equipment_type)
                : item.equipment_type;
            equipmentType = eq?.title ?? '';
          } catch {
            equipmentType = item.equipment_type || '';
          }

          return {
            ...item,
            equipment_type: equipmentType,
          };
        });

        setData(movements);
        setRowCount(response.data.data.total);
      } catch (error) {
        console.error('Error fetching equipment maintenance data:', error);
        getToast({
          status: 'error',
          title:
            error.response?.data?.message ||
            'Error loading equipment maintenance data',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [paginationModel, filterModel]);

  const locale = useTableLocale();

  const initialColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'visit_id',
      headerName: 'Ticket',
      headerAlign: 'left',
      align: 'left',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'number',
      filterOperators: getGridNumericOperators().filter(
        (operator) => operator.value !== 'isAnyOf'
      ),
      renderCell: ({ row }) => {
        console.log(row.visit_id);

        return (
          <ExternalLinkWithIcon
            path={`/visits/details/${row?.visit_id}`}
            text={row?.visit_id}
          />
        );
      },
    },
    {
      field: 'oxxo_ticket',
      headerName: 'Ticket',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <TextField
                label='Valor'
                placeholder='Valor del filtro'
                variant='standard'
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  applyValue({ ...item, value: e.target.value });
                }}
                InputLabelProps={{ shrink: true }}
              />
            );
          },
        },
        ...getGridStringOperators().filter((o) => o.value !== 'isAnyOf'),
      ],
    },
    {
      field: 'client_name',
      headerName: 'Plaza',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      filterOperators: [
        {
          value: 'equals',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Plaza'
                endpoint={`${GlobalVariables.getURL()}listClients`}
                labelKey='business_name'
              />
            );
          },
        },
        {
          value: 'not',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Plaza'
                endpoint={`${GlobalVariables.getURL()}listClients`}
                labelKey='business_name'
              />
            );
          },
        },
        {
          value: 'isAnyOf',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Plaza'
                endpoint={`${GlobalVariables.getURL()}listClients`}
                dataPath='data'
                labelKey='business_name'
                isMultiple
              />
            );
          },
        },
        {
          value: 'isEmpty',
        },
        {
          value: 'isNotEmpty',
        },
      ],
    },
    {
      field: 'cr',
      headerName: 'CR',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'site_name',
      headerName: 'Sucursal',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      filterOperators: [
        {
          value: 'equals',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Sucursal'
                endpoint={`${GlobalVariables.getURL()}listClientSites?id=all`}
                labelKey='site_name'
                idKey='id'
                dataPath='data.sites'
                useIdAsValue
              />
            );
          },
        },
        {
          value: 'not',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Sucursal'
                endpoint={`${GlobalVariables.getURL()}listClientSites?id=all`}
                labelKey='site_name'
                idKey='id'
                dataPath='data.sites'
                useIdAsValue
              />
            );
          },
        },
        {
          value: 'isEmpty',
        },
        {
          value: 'isNotEmpty',
        },
      ],
    },
    {
      field: 'os_num',
      headerName: 'Número de OS',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
      type: 'string',
    },
    {
      field: 'location',
      headerName: 'Ubicación',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'technical_name',
      headerName: 'Técnico asignado',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      filterOperators: [
        {
          value: 'equals',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Técnico asignado'
                endpoint={`${GlobalVariables.getURL()}listTechs`}
                labelKey='full_name'
                idKey='id'
                useIdAsValue
              />
            );
          },
        },
        {
          value: 'not',
          InputComponent: (props) => {
            return (
              <AutocompleteFilterInput
                {...props}
                label='Técnico asignado'
                endpoint={`${GlobalVariables.getURL()}listTechs`}
                labelKey='full_name'
                idKey='id'
                useIdAsValue
              />
            );
          },
        },
        {
          value: 'isEmpty',
        },
        {
          value: 'isNotEmpty',
        },
      ],
    },
    {
      field: 'equipment_type',
      headerName: 'Equipo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'serial_number',
      headerName: '# Serie',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'asset_number',
      headerName: '# Activo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'brand',
      headerName: 'Marca',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'model',
      headerName: 'Modelo / Tipo Rack',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'equipment_year',
      headerName: 'Año',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'movement_cause',
      headerName: 'Causa del movimiento',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        'Daño',
        'Garantía',
        'Otro',
        'Rentec',
        'Sucursal en remodelación',
        'Sucursal nueva',
        'Mantenimiento preventivo',
        'N/A',
      ],
    },
    {
      field: 'movement_type',
      headerName: 'Tipo de movimiento',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Instalaciones', 'Retiros', 'Mantenimiento preventivo'],
    },
    {
      field: 'service_date',
      headerName: 'Fecha y hora del servicio',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'date',
      valueGetter: ({ value }) => {
        return moment(value, 'DD/MM/YYYY HH:mm').toDate();
      },
      valueFormatter: ({ value }) => {
        return moment(value).format('DD/MM/YYYY HH:mm');
      },
      filterOperators: [
        ...getGridDateOperators(),
        {
          value: 'between',
          label: 'entre',
          InputComponent: ({ item, applyValue }) => {
            const [dateRanges, setDateRanges] = useState(
              item.value
                ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                : [null, null]
            );

            const handleRangeChange = (newValue) => {
              const formattedRange = newValue.map((date) =>
                date ? moment(date).format('YYYY-MM-DD') : null
              );

              setDateRanges(formattedRange);
              applyValue({
                ...item,
                value: formattedRange,
              });
            };

            return (
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={moment.locale()}
              >
                <DateRangePicker
                  label='Seleccionar rango de fechas'
                  value={dateRanges.map((date) => (date ? moment(date) : null))}
                  onChange={handleRangeChange}
                  localeText={{ start: 'Inicio', end: 'Fin' }}
                  slotProps={{ textField: { variant: 'standard' } }}
                />
              </LocalizationProvider>
            );
          },
        },
      ],
    },
    {
      field: 'start_job_date',
      headerName: 'Fecha y hora de inicio de trabajo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'end_job_date',
      headerName: 'Fecha de fin de trabajo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            params.row[col.field] ? params.row[col.field] : '-',
        }
  );

  const [columns, updateColumnOrder] = usePersistedColumnOrder(
    'equipMaintenanceTable',
    initialColumns
  );

  /**
   * Handles column order changes for the table.
   * @param {Object} params - Parameters containing target and old index.
   */
  const handleColumnOrderChange = (params) => {
    const { targetIndex, oldIndex } = params;

    const updatedColumns = [...columns];
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);

    updateColumnOrder(updatedColumns); // Update column order in persisted state
  };

  const handleFilterModelChange = async (filterModel) => {
    setFilterModel(filterModel);
    setIsFetching(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}equipment-maintenance/movements`;
      const response = await customAxios.post(
        endpoint,
        {
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
          filters: filterModel,
        },
        HEADERS
      );
      if (response.status === 204) {
        setData([]);
        setRowCount(0);
        return;
      }

      setData(response.data.data.movements);
      setRowCount(response.data.data.total);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handlePageModelChange = async (paginationModel) => {
    setPaginationModel(paginationModel);
    setIsFetching(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}equipment-maintenance/movements`;
      const response = await customAxios.post(
        endpoint,
        {
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
          filters: filterModel,
        },
        HEADERS
      );

      if (response.status === 204) {
        setData([]);
        setRowCount(0);
        return;
      }

      setData(response.data.data.movements);
      setRowCount(response.data.data.total);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleExport = async () => {
    getToast({
      status: 'success',
      title: 'Descargando archivo...',
    });
    const endpoint = `${GlobalVariables.getURL()}equipment-maintenance/movements/export`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
        body: JSON.stringify({
          filters: filterModel,
        }),
      });

      if (!response.ok) {
        getToast({ status: 'error', title: 'Error al descargar el archivo' });
        return;
      }

      // Obtain file name
      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename="(.+)"/);
      const filename =
        match?.[1] ||
        `export-mantenimientos_equipos_${
          new Date().toISOString().split('T')[0]
        }.xlsx`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // or use the filename from response headers
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      getToast({
        status: 'success',
        title: 'Archivo descargado correctamente',
      });
    } catch (err) {
      console.error('Download failed:', err);
      getToast({
        status: 'error',
        title: 'ERROR: Error al descargar el archivo',
      });
    }
  };

  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{ justifyContent: 'space-between', display: 'flex' }}
    >
      <div>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {permissions.includes('Export Excel') && <Button
          onClick={handleExport}
          variant='text'
          color='primary'
          size='small'
          startIcon={
            <svg
              focusable='false'
              aria-hidden='true'
              width={24}
              height={24}
              viewBox='0 0 24 24'
              style={{ fill: '#003087' }}
            >
              <path d='M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z'></path>
            </svg>
          }
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: 'primary.main',
            '& .MuiButton-startIcon': {
              mr: 1,
            },
            minWidth: 0,
            paddingX: 1,
            paddingY: 0.5,
          }}
        >
          Exportar
        </Button>}
      </div>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );

  return (
    <>
      <div className='datatable-btns-container'>
        <div className='dt-btn-main-container'>
          <span className='dabiWebText'>Mantenimiento a equipos</span>
        </div>
      </div>

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        loading={isFetching}
        rows={data}
        rowCount={rowCount}
        columns={columns}
        filterMode='server'
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        onPaginationModelChange={handlePageModelChange}
        onColumnOrderChange={(params) => handleColumnOrderChange(params)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          pinnedColumns: { right: ['action'] },
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              No hay datos en la base de datos
            </Stack>
          ),
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
    </>
  );
};
